import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { dmrApi } from '../api/dmr';
import { useAuth } from '../hooks/useAuth';

/**
 * DMR booking for franchisees (#246). The season API answers 403 until the
 * season is open to them (Nov 16, or "Everyone" in Setup), so these stay
 * hidden until then without a date in the client.
 */
export function useDmrOpenToMe(): boolean {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ['dmr', 'season'],
    queryFn: dmrApi.season,
    enabled: user?.role === 'franchisee',
    staleTime: 10 * 60 * 1000,
    retry: (n, e) => !(e instanceof ApiError) && n < 1,
  });
  return user?.role === 'franchisee' && !!q.data;
}

const DISMISS_KEY = 'amada_dmr_banner_dismissed_at';
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

/** Nudge to book, until every partner the franchisee belongs to has a call.
 *  Dismissing snoozes it for a week rather than for good: the 80% goal
 *  depends on people actually booking. */
export default function DmrBookingBanner() {
  const open = useDmrOpenToMe();
  const partners = useQuery({ queryKey: ['dmr', 'partners'], queryFn: dmrApi.partners, enabled: open });
  const [dismissedAt, setDismissedAt] = useState<number>(() => {
    try {
      return Number(localStorage.getItem(DISMISS_KEY)) || 0;
    } catch {
      return 0;
    }
  });

  if (!open || !partners.data) return null;
  if (Date.now() - dismissedAt < SNOOZE_MS) return null;
  const unbooked = partners.data.partners.some(
    (p) => !p.booking || p.booking.status === 'cancelled',
  );
  if (!unbooked) return null;

  const dismiss = () => {
    const now = Date.now();
    try {
      localStorage.setItem(DISMISS_KEY, String(now));
    } catch {
      /* storage blocked: the banner just won't stay dismissed */
    }
    setDismissedAt(now);
  };

  return (
    <div className="miab-banner" role="status">
      <span className="miab-banner-icon" aria-hidden="true">📅</span>
      <span className="miab-banner-text">
        Book your <strong>2027 Digital Marketing Review</strong>: 90 minutes with the marketing team on your
        leads, new website chat, reviews and results.{' '}
        <Link to="/dmr" className="miab-banner-link">
          Pick a time
        </Link>
      </span>
      <button type="button" className="miab-banner-close" onClick={dismiss} aria-label="Remind me later" title="Remind me later">
        ×
      </button>
    </div>
  );
}
